// MARK://dhk8/pos/features/ProductDetailDialog
// Store-style ("Amazon-ish") product detail for /menu, adapted to the 534×738 POS
// sheet. Built on the BottomSheet primitive (the established modal pattern) + `--pos-*`
// tokens → theme-aware, ≥44pt targets, internal scroll.
//
// Shows: hero photo (or a tasteful tinted placeholder with the food icon when none),
// name / SKU / description / price, a stock badge (Disponible / Bajo / Agotado, with an
// optional unit count for tracked items), a "Tendencia" chip (↑/→/↓) + a "★ Más
// vendido" badge for top sellers, and — for "personalizable" products — the variation
// and option groups (read-only: this is a browse view, not the order builder).
//
// Data: enriched via catalog.buildProductDetail() — display fields + variant/modifier
// groups come from the preview seed (src/lib/seed-preview.ts); outside the preview the
// extra maps are empty, so the dialog gracefully shows just the core fields.

import { useMemo, useState } from 'react'
import { Sparkles, Star, TrendingDown, TrendingUp, UtensilsCrossed } from 'lucide-react'
import type { ProductWithStock } from '@dockethub/ui-restaurant'
import { buildProductDetail } from '../lib/clients/catalog.js'
import { formatCents } from '../lib/money.js'
import { t } from '../i18n/es-MX.js'
import BottomSheet from './pos/BottomSheet.js'

export interface ProductDetailDialogProps {
  /** The product to show. Null/closed ⇒ nothing renders. */
  product: ProductWithStock | null
  onClose: () => void
}

type StockTone = 'ok' | 'low' | 'out' | 'untracked'

function stockTone(p: ProductWithStock): StockTone {
  if (p.isAvailable === false || p.stockStatus === 'OUT') return 'out'
  if (p.stockStatus === 'LOW') return 'low'
  if (p.stockStatus === 'OK') return 'ok'
  return 'untracked'
}

const STOCK_LABEL: Record<StockTone, string> = {
  ok: t('menu.stockOk'),
  low: t('menu.stockLow'),
  out: t('menu.stockOut'),
  untracked: t('menu.stockUntracked'),
}

export default function ProductDetailDialog({ product, onClose }: ProductDetailDialogProps) {
  const detail = useMemo(() => (product ? buildProductDetail(product) : null), [product])
  const [imgFailed, setImgFailed] = useState(false)

  if (!product || !detail) return null

  const tone = stockTone(product)
  const { display, extras } = detail
  const showImage = !!display.imageUrl && !imgFailed
  const customizable = !!extras.variants || (extras.modifierGroups?.length ?? 0) > 0
  const units = display.unitsOnHand ?? product.availablePortions ?? null
  const showUnits = (tone === 'ok' || tone === 'low') && typeof units === 'number'

  const trend = display.trend
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : null
  const trendLabel = trend === 'up' ? t('menu.trendUp') : trend === 'down' ? t('menu.trendDown') : t('menu.trendFlat')

  return (
    <BottomSheet
      open
      onClose={onClose}
      ariaLabel={product.name}
      heightFraction={0.92}
      centerOnDesktop
      title={
        <span className="dhk8-pdd__title">
          {product.name}
          {customizable && (
            <span className="dhk8-pdd__chip dhk8-pdd__chip--tint"><Sparkles size={11} /> {t('menu.customizable')}</span>
          )}
        </span>
      }
    >
      <div className="dhk8-pdd">
        {/* Hero photo / placeholder */}
        <div className="dhk8-pdd__hero">
          {showImage ? (
            <img src={display.imageUrl} alt="" loading="lazy" onError={() => setImgFailed(true)} />
          ) : (
            <div className="dhk8-pdd__hero-ph" aria-hidden>
              <UtensilsCrossed size={40} />
            </div>
          )}
          {display.bestSeller && (
            <span className="dhk8-pdd__hero-badge"><Star size={11} fill="currentColor" /> {t('menu.bestSeller')}</span>
          )}
        </div>

        {/* Name / SKU / price */}
        <div className="dhk8-pdd__head">
          <div className="dhk8-pdd__head-main">
            <h2 className="dhk8-pdd__name">{product.name}</h2>
            <span className="dhk8-pdd__sku">{product.sku}</span>
          </div>
          <span className="dhk8-pdd__price">{formatCents(product.unitPriceCents)}</span>
        </div>

        {/* Status row: stock + trend */}
        <div className="dhk8-pdd__status">
          <span className={`dhk8-pdd__badge dhk8-pdd__badge--${tone}`}>
            <span className="dhk8-pdd__dot" aria-hidden />
            {STOCK_LABEL[tone]}
            {showUnits && <span className="dhk8-pdd__badge-count">· {units} {units === 1 ? 'unidad' : 'unidades'}</span>}
          </span>
          <span className={`dhk8-pdd__chip dhk8-pdd__chip--trend-${trend ?? 'flat'}`}>
            {TrendIcon ? <TrendIcon size={12} /> : <span className="dhk8-pdd__chip-arrow" aria-hidden>→</span>}
            <span className="dhk8-pdd__chip-lbl">{t('menu.trend')}</span> {trendLabel}
          </span>
        </div>

        {/* Description */}
        {display.description && <p className="dhk8-pdd__desc">{display.description}</p>}

        {/* Variations / options — read-only */}
        {customizable && (
          <div className="dhk8-pdd__opts">
            <div className="dhk8-pdd__opts-head">
              <span className="dhk8-pdd__opts-title">{t('menu.options')}</span>
              <span className="dhk8-pdd__opts-hint">{t('menu.optionsHint')}</span>
            </div>

            {extras.variants && (
              <div className="dhk8-pdd__group">
                <div className="dhk8-pdd__group-head">
                  <span className="dhk8-pdd__group-name">{extras.variants.name}</span>
                  <span className="dhk8-pdd__group-meta">{t('menu.chooseOne')}</span>
                </div>
                <div className="dhk8-pdd__opts-list">
                  {extras.variants.options.map((o) => (
                    <span key={o.id} className="dhk8-pdd__opt">
                      <span className="dhk8-pdd__opt-name">{o.name}</span>
                      {o.priceDeltaCents !== 0 && (
                        <span className="dhk8-pdd__opt-delta">{o.priceDeltaCents > 0 ? '+' : '−'}{formatCents(Math.abs(o.priceDeltaCents))}</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(extras.modifierGroups ?? []).map((g) => (
              <div key={g.id} className="dhk8-pdd__group">
                <div className="dhk8-pdd__group-head">
                  <span className="dhk8-pdd__group-name">{g.name}</span>
                  <span className="dhk8-pdd__group-meta">
                    {g.isRequired ? `${t('menu.required')} · ` : ''}
                    {g.maxSelect === 1 ? t('menu.chooseOne') : g.minSelect === g.maxSelect ? `${t('menu.choose')} ${g.maxSelect}` : `${t('menu.upTo')} ${g.maxSelect}`}
                  </span>
                </div>
                <div className="dhk8-pdd__opts-list">
                  {g.options.map((o) => (
                    <span key={o.id} className="dhk8-pdd__opt">
                      <span className="dhk8-pdd__opt-name">{o.name}</span>
                      {o.priceDeltaCents !== 0 && <span className="dhk8-pdd__opt-delta">+{formatCents(o.priceDeltaCents)}</span>}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{styles}</style>
    </BottomSheet>
  )
}

const styles = `
  .dhk8-pdd__title { display:inline-flex; align-items:center; gap:var(--pos-space-2,8px); flex-wrap:wrap; }
  .dhk8-pdd { display:flex; flex-direction:column; gap:var(--pos-space-4,16px); padding:var(--pos-space-2,8px) var(--pos-space-4,16px) var(--pos-space-5,20px); }

  .dhk8-pdd__hero { position:relative; aspect-ratio:16/10; border-radius:var(--pos-radius-lg,12px); overflow:hidden; background:var(--pos-tint-soft,rgba(31,139,58,.12)); }
  .dhk8-pdd__hero img { width:100%; height:100%; object-fit:cover; display:block; }
  .dhk8-pdd__hero-ph { width:100%; height:100%; display:flex; align-items:center; justify-content:center; color:var(--pos-tint,#1F8B3A); opacity:.7; background:linear-gradient(135deg, var(--pos-tint-soft,rgba(31,139,58,.12)), color-mix(in oklch, var(--pos-tint,#1F8B3A) 6%, transparent)); }
  .dhk8-pdd__hero-badge { position:absolute; top:var(--pos-space-2,8px); left:var(--pos-space-2,8px); display:inline-flex; align-items:center; gap:4px; padding:3px var(--pos-space-2,8px); border-radius:var(--pos-radius-pill,999px); background:var(--pos-surface,#161C18); color:var(--pos-warning,#F1B33B); font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-semibold,600); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }

  .dhk8-pdd__head { display:flex; align-items:flex-start; justify-content:space-between; gap:var(--pos-space-3,12px); }
  .dhk8-pdd__head-main { display:flex; flex-direction:column; gap:2px; min-width:0; }
  .dhk8-pdd__name { margin:0; font-family:var(--font-display,system-ui); font-size:var(--pos-text-xl,20px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); line-height:1.2; }
  .dhk8-pdd__sku { font-family:var(--font-mono,ui-monospace,monospace); font-size:var(--pos-text-xs,12px); letter-spacing:.04em; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-pdd__price { flex-shrink:0; font-family:var(--font-mono,ui-monospace,monospace); font-size:var(--pos-text-xl,20px); font-weight:var(--pos-weight-semibold,600); font-variant-numeric:tabular-nums; color:var(--pos-success,#8BC34A); }

  .dhk8-pdd__status { display:flex; align-items:center; gap:var(--pos-space-2,8px); flex-wrap:wrap; }
  .dhk8-pdd__badge { display:inline-flex; align-items:center; gap:6px; padding:4px var(--pos-space-3,12px); border-radius:var(--pos-radius-pill,999px); font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); }
  .dhk8-pdd__dot { width:7px; height:7px; border-radius:var(--radius-dot); background:currentColor; }
  .dhk8-pdd__badge-count { font-family:var(--font-mono,ui-monospace,monospace); font-variant-numeric:tabular-nums; font-weight:var(--pos-weight-regular,400); font-size:var(--pos-text-xs,12px); }
  .dhk8-pdd__badge--ok { background:var(--pos-tint-soft,rgba(31,139,58,.12)); color:var(--pos-tint,#1F8B3A); }
  .dhk8-pdd__badge--low { background:var(--pos-warning-bg,rgba(241,179,59,.16)); color:var(--pos-warning,#F1B33B); }
  .dhk8-pdd__badge--out { background:var(--pos-danger-bg,rgba(229,72,77,.16)); color:var(--pos-danger,#E5484D); }
  .dhk8-pdd__badge--untracked { background:var(--pos-elevated,#1E2520); color:var(--pos-fg-tertiary,#7E8C82); }

  .dhk8-pdd__chip { display:inline-flex; align-items:center; gap:5px; padding:4px var(--pos-space-3,12px); border-radius:var(--pos-radius-pill,999px); font-size:var(--pos-text-xs,12px); font-weight:var(--pos-weight-medium,500); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); color:var(--pos-fg-secondary,#B6BFB8); }
  .dhk8-pdd__chip--tint { border:none; padding:1px var(--pos-space-2,8px); background:var(--pos-tint-soft,rgba(31,139,58,.12)); color:var(--pos-tint,#1F8B3A); font-size:9px; font-weight:var(--pos-weight-semibold,600); letter-spacing:.06em; text-transform:uppercase; }
  .dhk8-pdd__chip-lbl { color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-pdd__chip-arrow { font-weight:var(--pos-weight-semibold,600); }
  .dhk8-pdd__chip--trend-up { color:var(--pos-tint,#1F8B3A); border-color:color-mix(in oklch, var(--pos-tint,#1F8B3A) 30%, transparent); }
  .dhk8-pdd__chip--trend-down { color:var(--pos-danger,#E5484D); border-color:color-mix(in oklch, var(--pos-danger,#E5484D) 30%, transparent); }

  .dhk8-pdd__desc { margin:0; font-size:var(--pos-text-md,15px); line-height:1.5; color:var(--pos-fg-secondary,#B6BFB8); }

  .dhk8-pdd__opts { display:flex; flex-direction:column; gap:var(--pos-space-3,12px); padding-top:var(--pos-space-3,12px); border-top:1px solid var(--pos-separator,rgba(255,255,255,.07)); }
  .dhk8-pdd__opts-head { display:flex; align-items:baseline; justify-content:space-between; gap:var(--pos-space-2,8px); }
  .dhk8-pdd__opts-title { font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); }
  .dhk8-pdd__opts-hint { font-size:var(--pos-text-xxs,11px); color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-pdd__group { display:flex; flex-direction:column; gap:var(--pos-space-2,8px); }
  .dhk8-pdd__group-head { display:flex; align-items:baseline; justify-content:space-between; gap:var(--pos-space-2,8px); }
  .dhk8-pdd__group-name { font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); color:var(--pos-fg,#E8EFE9); }
  .dhk8-pdd__group-meta { font-size:var(--pos-text-xxs,11px); color:var(--pos-fg-tertiary,#7E8C82); white-space:nowrap; }
  .dhk8-pdd__opts-list { display:flex; flex-wrap:wrap; gap:var(--pos-space-2,8px); }
  .dhk8-pdd__opt { display:inline-flex; align-items:center; gap:6px; padding:5px var(--pos-space-3,12px); border-radius:var(--pos-radius-md,8px); background:var(--pos-elevated,#1E2520); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); }
  .dhk8-pdd__opt-name { font-size:var(--pos-text-sm,13px); color:var(--pos-fg,#E8EFE9); }
  .dhk8-pdd__opt-delta { font-family:var(--font-mono,ui-monospace,monospace); font-size:var(--pos-text-xxs,11px); font-variant-numeric:tabular-nums; color:var(--pos-tint,#1F8B3A); }
`
